<script setup lang="ts">
import { QExpansionItem } from 'quasar'
import { Color } from '@previo/codebooks'
import PDivider from '../divider/PDivider.vue'
import PIcon from '../icon/PIcon.vue'
import PSpace from '../space/PSpace.vue'
import { PSpaceSize } from '../space/types'
import { PMaxLines } from '../typography/types'
import PTextLink from '../typography/links/PTextLink.vue'
import PPromotedBadge from '../badge/PPromotedBadge.vue'
import { PGlobalMenuItem, PGlobalMenuWithSubMenu } from './types'

type Props = {
  items: (PGlobalMenuWithSubMenu | PGlobalMenuItem)[]
  menuIndex: number
}

type Emits = {
  (e: 'closeMenu'): void
}

type Slots = {
  'side-menu-item'(props: { item: PGlobalMenuItem }): unknown
}

withDefaults(defineProps<Props>(), {})

const emit = defineEmits<Emits>()

defineSlots<Slots>()

const isWithoutSubMenu = (item: PGlobalMenuWithSubMenu | PGlobalMenuItem): boolean => {
  return item instanceof PGlobalMenuItem
}

const itemRoute = (item: PGlobalMenuWithSubMenu | PGlobalMenuItem): null | string => {
  return item instanceof PGlobalMenuItem ? item.route : null
}

const isMenuItem = (item: PGlobalMenuWithSubMenu | PGlobalMenuItem): item is PGlobalMenuItem => {
  return item instanceof PGlobalMenuItem
}
</script>

<template>
  <template v-for="(item, n) in items" :key="n">
    <p-divider v-if="n === 0 && menuIndex > 1" />

    <template v-if="item.isVisible">
      <q-expansion-item :group="`sub-menu-${menuIndex}`" hide-expand-icon :expand-separator="false">
        <template #header="{ expanded }">
          <component
            :is="isWithoutSubMenu(item) ? 'router-link' : 'div'"
            v-bind="isWithoutSubMenu(item) ? { to: { name: itemRoute(item) ?? undefined } } : {}"
            class="p-flex p-flex-center p-full-width"
            @click="isWithoutSubMenu(item) ? emit('closeMenu') : null"
          >
            <p-space v-for="level in menuIndex - 1" :key="level" :mobile="PSpaceSize.SM" />

            <template v-if="!isWithoutSubMenu(item)">
              <p-icon v-if="item.icon" :icon="item.icon" :color="Color.PRIMARY" />
              <p-space :mobile="PSpaceSize.SM" />

              <p-text-link
                :text="item.title"
                :color="Color.GREY_600"
                :bold="expanded"
                :max-lines="PMaxLines.ONE"
              />
              <template v-if="item.isPromoted">
                <p-space :mobile="PSpaceSize.XS" />
                <p-promoted-badge />
              </template>
              <p-space :mobile="PSpaceSize.GROW" />

              <p-icon :icon="expanded ? 'chevron-up' : 'chevron-down'" :color="Color.GREY_500" />
            </template>

            <template v-else>
              <template v-if="isMenuItem(item) && $slots['side-menu-item']">
                <slot name="side-menu-item" :item="item" />
              </template>

              <template v-else>
                <p-icon v-if="item.icon" :icon="item.icon" :color="Color.PRIMARY" />
                <p-space :mobile="PSpaceSize.SM" />

                <p-text-link
                  :text="item.title"
                  :color="Color.GREY_600"
                  :max-lines="PMaxLines.ONE"
                />
                <template v-if="item.isPromoted">
                  <p-space :mobile="PSpaceSize.XS" />
                  <p-promoted-badge />
                </template>
                <p-space :mobile="PSpaceSize.GROW" />
              </template>
            </template>
          </component>
        </template>

        <p-side-menu-expansion
          v-if="item instanceof PGlobalMenuWithSubMenu"
          :menu-index="menuIndex + 1"
          :items="item.items"
          @close-menu="emit('closeMenu')"
        >
          <template
            v-if="$slots['side-menu-item']"
            #side-menu-item="{ item: subItem }: { item: PGlobalMenuItem }"
          >
            <slot name="side-menu-item" :item="subItem" />
          </template>
        </p-side-menu-expansion>
      </q-expansion-item>

      <p-divider class="ds-global-navigation-mobile-divider" />
    </template>
  </template>
</template>

<style lang="scss" scoped>
.ds-global-navigation-mobile-divider:last-of-type {
  display: none;
}
</style>
